import React from 'react';
import { useNavigate } from 'react-router';
import { Bell, Search, User, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import FormControl from '../../components/common/FormControl';

interface CSuiteHeaderProps {
    onToggleSidebar?: () => void;
}

const LeadershipHeader: React.FC<CSuiteHeaderProps> = ({ onToggleSidebar }) => {
    const { logout, state } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        console.log('C-Suite Logout clicked');
        try {
            await logout();
        } catch (error) {
            console.error('C-Suite Logout error:', error);
        }
    };

    React.useEffect(() => {
        if (!state.isAuthenticated) {
            console.log('User not authenticated, redirecting to login...');
            window.location.href = '/login';
        }
    }, [state.isAuthenticated]);

    return (
        <header className="c-suite-header" style={{
            height: '70px',
            background: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1000
        }}>
            <div className="flex items-center gap-4">
                <button
                    data-testid="BTN_leadership_header_toggle_sidebar"
                    onClick={onToggleSidebar}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                    title="Toggle Menu"
                >
                    <Menu size={24} />
                </button>

                {/* Logo */}
                <div style={{ height: '70px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        backgroundColor: '#f3e5f5', // Light Purple Tint
                        borderRadius: '50px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '44px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                        overflow: 'hidden'
                    }}>
                        <img
                            src={process.env.PUBLIC_URL + '/anygrc-logo.png'}
                            alt="AnyGRC Leadership"
                            style={{
                                height: '100%',
                                width: 'auto',
                                objectFit: 'contain',
                                mixBlendMode: 'multiply',
                                filter: 'hue-rotate(65deg) brightness(0.9)' // Blue -> Purple
                            }}
                        />
                    </div>
                </div>

                {/* Persona Icon */}
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">👑</span>
                </div>

                {/* Persona Text */}
                <div>
                    <span className="text-sm font-bold text-purple-100 uppercase tracking-wider">LEADERSHIP DASHBOARD</span>
                    {state.user?.entity_name && (
                        <span className="ml-3 pl-3 border-l border-purple-100/30 text-purple-50 font-medium">
                            {state.user.entity_name}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex-1 max-w-xl mx-12">
                <FormControl.Input
                    type="text"
                    placeholder="Search insights, risks, or reports..."
                    leadingIcon={<Search className="text-purple-200" size={18} />}
                    className="fi-input--header"
                />
            </div>

            <div className="flex items-center gap-6">
                <button className="relative text-purple-100 hover:text-white transition-colors">
                    <Bell size={20} />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>

                <div className="h-8 w-[1px] bg-purple-400/30"></div>

                <div className="flex items-center gap-3">
                    <div className="text-right hidden md:block">
                        <div className="text-sm font-semibold">{state.user?.user_name || 'Executive'}</div>
                        <div className="text-xs text-purple-200">Leadership Team</div>
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white/20">
                        <User size={20} className="text-white" />
                    </div>
                    <button
                        data-testid="BTN_leadership_header_logout"
                        onClick={handleLogout}
                        className="ml-2 text-purple-200 hover:text-white transition-colors"
                        title="Sign Out"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default LeadershipHeader;
